import styled from '@emotion/styled'
import type { FC, PropsWithChildren } from 'react'
import NextLink from 'next/link'
import { css } from '@emotion/react'

import Typography from '@segment/matcha/components/Typography'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import IntegrationCardTemplate from 'views/Catalog/components/IntegrationCard'

import type { Hit } from '@segment/api/algolia/client'

import type { FetchedCatalogIntegration } from '@segment/api/routes/catalog/queries'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

interface SimilarIntegrationsProps {
  entries: Array<Hit<FetchedCatalogIntegration>>
}

const copy = {
  heading: `Similar integrations`,
}

const link = {
  text: `View all integrations`,
  url: `/catalog/`,
}

const SimilarIntegrations: FC<PropsWithChildren<SimilarIntegrationsProps>> = ({ entries }) => {
  const pageConfig = usePageConfig()

  return (
    <Wrapper>
      <Heading variant="h2" tag="h2">
        {copy.heading}
      </Heading>
      <StyledCardsGrid maxItems={entries.length}>
        {entries?.map((integration) => (
          <IntegrationCardTemplate key={integration.objectID} integration={integration} />
        ))}
      </StyledCardsGrid>
      <NextLink href={getAbsoluteUrl(link.url, pageConfig)}>
        <ButtonText variant="primary" size="medium" tag="span" iconEnd={ChevronRightIcon}>
          {link.text}
        </ButtonText>
      </NextLink>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  background: ${({ theme }) => theme.palette.twilio.gray[10]};
  padding: 56px 24px 0;
  display: grid;
  justify-items: center;
  gap: 32px;
  grid-template-columns: 1fr;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    padding: 80px 0 0;
    gap: 56px;
    justify-items: center;
    grid-template-columns: unset;
  }
`

interface StyledCardsGridProps {
  maxItems: number
}
const StyledCardsGrid = styled.div<StyledCardsGridProps>`
  display: grid;
  gap: 32px;

  ${({ theme, maxItems }) =>
    maxItems > 1 &&
    css`
      ${theme.breakpoints.up(`sm`)} {
        justify-content: center;
        grid-template-columns: repeat(2, 220px);
      }
    `}

  ${({ theme }) => theme.breakpoints.up(`xl`)} {
    grid-auto-flow: column;
    grid-auto-columns: 220px;
  }
`

const Heading = styled(Typography)`
  text-align: center;
  margin: 0;
`

export default SimilarIntegrations
